/**
 * Tidal gate finder — when is the door open?
 *
 * Lots of passages have a gate: a sill or bar you can only cross above a certain
 * height of tide, or a narrows you only want to take while the stream is with you
 * (or at least not punching hard against you). Rather than bake in one rule, this
 * walks a time window sampling a caller-supplied "is the gate open now?" test and
 * returns the contiguous open windows. The caller builds `isOpen` from whatever
 * model it has (height of tide, stream rate/direction, or both), so this stays
 * pure and testable. Window edges are at the sampling resolution, so a tight gate
 * wants a small step.
 */
export interface GateWindow {
  startMs: number;
  endMs: number;
}

export function findGateWindows(
  startMs: number,
  endMs: number,
  stepMin: number,
  isOpen: (ms: number) => boolean,
): GateWindow[] {
  const stepMs = Math.max(1, stepMin) * 60_000;
  const windows: GateWindow[] = [];
  let cur: GateWindow | null = null;
  for (let t = startMs; t <= endMs; t += stepMs) {
    if (isOpen(t)) {
      if (!cur) cur = { startMs: t, endMs: t };
      else cur.endMs = t;
    } else if (cur) {
      windows.push(cur);
      cur = null;
    }
  }
  if (cur) windows.push(cur);
  return windows;
}

/** The first window open at or after `nowMs` (the one to aim for). */
export function nextGateOpening(
  nowMs: number,
  endMs: number,
  stepMin: number,
  isOpen: (ms: number) => boolean,
): GateWindow | null {
  return findGateWindows(nowMs, endMs, stepMin, isOpen).find((w) => w.endMs >= nowMs) ?? null;
}

/** Whether a given instant falls inside any open window. */
export function gateOpenAt(windows: GateWindow[], ms: number): boolean {
  return windows.some((w) => ms >= w.startMs && ms <= w.endMs);
}
